import { motion } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ArrowUpRight, Scale, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";

const hubs = [
  {
    code: "AE",
    name: "United Arab Emirates",
    cities: "Abu Dhabi · Dubai",
    focus:
      "Family offices, sovereign co-invest, MENA venture. Digital asset licensing across ADGM and DIFC.",
    time: "GMT+4",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c",
    frameworks: ["ADGM · FSRA", "Dubai · VARA"],
  },
  {
    code: "SG",
    name: "Singapore",
    cities: "Marina Bay · VCC",
    focus:
      "APAC private markets, structuring, impact & climate. Digital asset services under MAS Payment Services Act.",
    time: "GMT+8",
    image: "https://images.pexels.com/photos/32020754/pexels-photo-32020754.jpeg",
    frameworks: ["MAS · PSA"],
  },
  {
    code: "IN",
    name: "India",
    cities: "Mumbai · GIFT City",
    focus:
      "Deal origination, technology diligence, next-gen advisory. Offshore-in-onshore structuring via GIFT City IFSC.",
    time: "GMT+5:30",
    image: "https://images.pexels.com/photos/5414582/pexels-photo-5414582.jpeg",
    frameworks: ["GIFT City · IFSCA"],
  },
];

const frameworks = [
  {
    slug: "adgm-fsra",
    region: "UAE · Abu Dhabi",
    code: "ADGM",
    authority: "FSRA",
    long_name: "Abu Dhabi Global Market · Financial Services Regulatory Authority",
    desc: "Abu Dhabi Global Market · Financial Services Regulatory Authority. Institutional fund & crypto activity licensing.",
    detail: {
      read: "45 sec",
      headline: "The MENA regulator of choice for institutional funds and virtual assets.",
      covers: [
        "Fund vehicles (Category 3C investment managers, VCs, PE funds)",
        "Virtual asset custody, exchanges & broker-dealers (FSRA VA framework)",
        "Multilateral trading facilities & OTC digital asset activity",
        "Family office SPCs, foundations, and private holding structures",
      ],
      doesnt: [
        "Retail-facing crypto or gambling activity",
        "Sanctions-linked jurisdictions or counterparties",
        "Unlicensed brokerage from ADGM-domiciled entities",
      ],
      matters:
        "ADGM's common-law jurisdiction and FSRA's mature VA framework give principals a defensible answer to boards, auditors, and LPs — without leaving the Gulf.",
    },
  },
  {
    slug: "dubai-vara",
    region: "UAE · Dubai",
    code: "VARA",
    authority: "DIFC-adjacent",
    long_name: "Virtual Assets Regulatory Authority",
    desc: "Virtual Assets Regulatory Authority. Dubai's dedicated regulator for institutional virtual asset services.",
    detail: {
      read: "45 sec",
      headline: "Dubai's dedicated regulator for institutional-grade virtual asset activity.",
      covers: [
        "VASP licensing across custody, exchange, broker-dealer, and advisory",
        "Investment token issuance & regulated marketing",
        "Institutional lending, borrowing, and staking activity",
        "Rulebooks aligned to FATF, IOSCO and MiCA principles",
      ],
      doesnt: [
        "Activity conducted from the DIFC free zone (which sits under DFSA)",
        "Purely retail exchange operations without proper VASP category",
        "Anonymous or privacy-coin denominated services",
      ],
      matters:
        "VARA gives principals a Dubai onshore licence that reads clean to counterparties, banks, and audit committees — and passports naturally into ADGM and DIFC counterparties.",
    },
  },
  {
    slug: "singapore-mas-psa",
    region: "Singapore",
    code: "MAS · PSA",
    authority: "MAS",
    long_name: "Monetary Authority of Singapore · Payment Services Act",
    desc: "Monetary Authority of Singapore · Payment Services Act. Digital token services & VCC fund structuring.",
    detail: {
      read: "45 sec",
      headline: "APAC's most respected financial regulator — for both funds and digital assets.",
      covers: [
        "Fund management licences (LFMC, RFMC) and VCC fund structures",
        "Digital Payment Token services under Payment Services Act",
        "Institutional custody, exchange, and OTC dealing licences",
        "Cross-border marketing to accredited & institutional investors",
      ],
      doesnt: [
        "Retail leveraged crypto trading or high-risk derivatives to public",
        "Yield products marketed to the retail public without exemption",
        "Anonymous privacy-coin services",
      ],
      matters:
        "A MAS-supervised structure is the APAC equivalent of a US or EU gold standard. LPs, banks and auditors treat it as a trust anchor across every ticket.",
    },
  },
  {
    slug: "india-gift-ifsca",
    region: "India",
    code: "GIFT City",
    authority: "IFSCA",
    long_name: "International Financial Services Centres Authority · GIFT City IFSC",
    desc: "International Financial Services Centres Authority. Offshore-in-onshore fund, banking & crypto activity.",
    detail: {
      read: "45 sec",
      headline: "India's offshore-in-onshore gateway — with fund, banking and digital-asset regimes.",
      covers: [
        "AIF Cat I / II / III funds (venture, PE, hedge) with tax-friendly IFSC treatment",
        "Foreign portfolio investment & family investment funds",
        "Bullion, insurance, aircraft leasing and payment-service activity",
        "Emerging digital-asset & tokenized-security regimes",
      ],
      doesnt: [
        "Onshore domestic-INR fund activity (governed by SEBI, not IFSCA)",
        "Retail unregulated crypto trading",
        "Cross-border activity outside the IFSC exemption perimeter",
      ],
      matters:
        "GIFT City lets Indian-heritage principals raise, invest, and hold capital offshore while remaining onshore — with tax and regulatory clarity that mainland structures do not offer.",
    },
  },
  {
    slug: "eu-mica-casp",
    region: "European Union",
    code: "MiCA · CASP",
    authority: "ESMA / NCAs",
    long_name: "Markets in Crypto-Assets · Crypto-Asset Service Provider",
    desc: "Markets in Crypto-Assets regulation. Passportable CASP authorisation across all 27 member states.",
    detail: {
      read: "45 sec",
      headline: "One EU-wide authorisation, passportable across all 27 member states.",
      covers: [
        "Custody & administration of crypto-assets for third parties",
        "Operation of trading platforms and OTC exchange of crypto-assets",
        "Advice, portfolio management, and execution of orders",
        "Issuance & offering of asset-referenced tokens (ARTs) and e-money tokens (EMTs)",
      ],
      doesnt: [
        "NFTs that are genuinely unique and non-fungible (out of scope)",
        "Fully decentralised protocols without an identifiable issuer",
        "Central-bank digital currencies (CBDCs)",
      ],
      matters:
        "MiCA gives principals a single, board-defensible EU passport for digital asset activity — replacing the patchwork of member-state regimes that made EU coverage impossible before.",
    },
  },
  {
    slug: "americas-genius-fintrac",
    region: "Americas",
    code: "GENIUS · FINTRAC",
    authority: "US · Canada",
    long_name: "US GENIUS Act · Canadian FINTRAC registration",
    desc: "US GENIUS Act (stablecoin & digital asset framework) and Canada's FINTRAC registration for regulated activity.",
    detail: {
      read: "45 sec",
      headline: "A working North-American answer for stablecoin, custody and MSB activity.",
      covers: [
        "US GENIUS Act stablecoin issuance & payment token framework",
        "Federal & state-level payment / trust-charter routes for custody",
        "Canada · FINTRAC MSB / FRMSB registration for VA services",
        "OSC / provincial-securities alignment for tokenized offerings",
      ],
      doesnt: [
        "US-registered broker-dealer or ATS activity (separate SEC / FINRA scope)",
        "Retail-facing lending or yield products in most US states",
        "Unregistered securities offerings to US persons",
      ],
      matters:
        "North America runs on a mosaic of regulators. GENIUS + FINTRAC together give principals a clean, coordinated set of answers for stablecoin, custody and MSB activity across the US and Canada.",
    },
  },
];

function generateFrameworkPdf(f) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;

  doc.setFillColor(244, 239, 230);
  doc.rect(0, 0, pageW, pageH, "F");

  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(2);
  doc.line(margin, margin, margin + 36, margin);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 110, 90);
  doc.text(
    `ADVIK VENTURES \u00B7 REGULATORY BRIEF \u00B7 ${f.region.toUpperCase()}`,
    margin,
    margin + 22
  );
  doc.text("FOR COUNSEL \u00B7 ILLUSTRATIVE", pageW - margin, margin + 22, {
    align: "right",
  });

  let y = margin + 80;
  doc.setFont("times", "italic");
  doc.setFontSize(36);
  doc.setTextColor(10, 15, 28);
  doc.text(f.code, margin, y);

  y += 22;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(150, 120, 40);
  doc.text(f.authority.toUpperCase(), margin, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(60, 55, 45);
  const nameLines = doc.splitTextToSize(f.long_name, pageW - margin * 2);
  doc.text(nameLines, margin, y);
  y += nameLines.length * 13;

  y += 20;
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.line(margin, y, margin + 24, y);
  y += 18;
  doc.setFont("times", "italic");
  doc.setFontSize(16);
  doc.setTextColor(10, 15, 28);
  const headline = doc.splitTextToSize(f.detail.headline, pageW - margin * 2);
  doc.text(headline, margin, y);
  y += headline.length * 20 + 20;

  const colW = (pageW - margin * 2 - 24) / 2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(4, 120, 87);
  doc.text("WHAT IT COVERS", margin, y);
  let leftY = y + 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  f.detail.covers.forEach((item) => {
    doc.setTextColor(4, 120, 87);
    doc.text("\u2713", margin, leftY);
    doc.setTextColor(10, 15, 28);
    const lines = doc.splitTextToSize(item, colW - 14);
    doc.text(lines, margin + 12, leftY);
    leftY += lines.length * 13 + 6;
  });

  const rightX = margin + colW + 24;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(150, 120, 40);
  doc.text("WHAT IT DOESN\u2019T", rightX, y);
  let rightY = y + 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  f.detail.doesnt.forEach((item) => {
    doc.setTextColor(160, 145, 115);
    doc.text("\u2014", rightX, rightY);
    doc.setTextColor(70, 60, 45);
    const lines = doc.splitTextToSize(item, colW - 14);
    doc.text(lines, rightX + 14, rightY);
    rightY += lines.length * 13 + 6;
  });

  y = Math.max(leftY, rightY) + 24;

  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(3);
  doc.line(margin, y - 4, margin, y + 70);
  doc.setLineWidth(0.5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(150, 120, 40);
  doc.text("WHY IT MATTERS", margin + 10, y + 4);
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  doc.setTextColor(10, 15, 28);
  const why = doc.splitTextToSize(f.detail.matters, pageW - margin * 2 - 14);
  doc.text(why, margin + 10, y + 24);

  const footY = pageH - 40;
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.5);
  doc.line(margin, footY - 14, pageW - margin, footY - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(120, 110, 90);
  doc.text(
    "ADVIK VENTURES \u00B7 avadh@advikvc.com \u00B7 CAPABILITY CENTER AS A SERVICE",
    margin,
    footY
  );
  doc.text("Illustrative brief \u00B7 not legal advice", pageW - margin, footY, {
    align: "right",
  });
  doc.text(
    `Slug: #frameworks/${f.slug}  \u00B7  ${new Date().toLocaleDateString()}`,
    margin,
    footY + 12
  );

  doc.save(`Advik-Framework-${f.code.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`);
}

export default function Regions() {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeFw, setActiveFw] = useState(null);
  const h = hubs[active];

  const openFramework = useCallback((idx) => {
    setActiveFw({ ...frameworks[idx], idx });
    setOpen(true);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#frameworks/${frameworks[idx].slug}`);
    }
  }, []);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v && typeof window !== "undefined") {
      const hash = window.location.hash;
      if (hash.startsWith("#frameworks/")) {
        window.history.replaceState(null, "", "#regions");
      }
    }
  };

  useEffect(() => {
    const openFromHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      const match = hash.match(/^frameworks\/([a-z0-9-]+)$/);
      if (!match) return;
      const idx = frameworks.findIndex((f) => f.slug === match[1]);
      if (idx >= 0) {
        setTimeout(() => {
          document
            .getElementById("regions")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
          openFramework(idx);
        }, 400);
      }
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [openFramework]);

  const copyLink = () => {
    if (!activeFw || typeof window === "undefined") return;
    const url = `${window.location.origin}${window.location.pathname}#frameworks/${activeFw.slug}`;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(
          () => toast.success("Link copied to clipboard"),
          () => toast.success("Link ready: " + url)
        );
      } else {
        toast.success("Link ready: " + url);
      }
    } catch (e) {
      toast.success("Link ready: " + url);
    }
  };

  const downloadBrief = () => {
    if (!activeFw) return;
    try {
      generateFrameworkPdf(activeFw);
      toast.success(`${activeFw.code} brief downloaded. Ready to send to counsel.`);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF. Please try again.");
    }
  };

  return (
    <section
      id="regions"
      className="relative py-24 md:py-40 border-t border-[var(--line)]"
      data-testid="regions-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
              V. Regional Execution
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Three operating hubs. <em className="text-gold not-italic">Six</em> regulatory frameworks.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 md:pt-6 text-ivory/70 leading-relaxed">
            Boots on the ground where principals sit — licensed under the
            regulators that matter across MENA, APAC, EU, and the Americas.
          </p>
        </div>

        {/* Operating hubs */}
        <div className="mb-6 flex items-baseline justify-between">
          <div className="text-[10px] tracking-eyebrow uppercase text-emerald flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald animate-pulse" />
            Operating hubs · 3
          </div>
          <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
            Local presence
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
            {hubs.map((rg, i) => (
              <button
                key={rg.code}
                onClick={() => setActive(i)}
                data-testid={`hub-${rg.code}`}
                className={`region-card text-left border p-6 md:p-7 relative ${
                  active === i
                    ? "border-gold bg-ink-3"
                    : "border-[var(--line)] bg-ink/30"
                }`}
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`font-display text-4xl md:text-5xl leading-none ${
                      active === i ? "text-gold" : "text-ivory-dim"
                    }`}
                  >
                    {rg.code}
                  </span>
                  <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                    {rg.time}
                  </span>
                </div>
                <div className="mt-6">
                  <div className="font-display text-xl text-ivory">{rg.name}</div>
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mt-1">
                    {rg.cities}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {rg.frameworks.map((f) => (
                      <span
                        key={f}
                        className={`text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 border ${
                          active === i
                            ? "border-gold/60 text-gold"
                            : "border-[var(--line-strong)] text-ivory-dim"
                        }`}
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
                {active === i && (
                  <motion.div
                    layoutId="hub-dot"
                    className="absolute -left-[1px] top-6 h-8 w-0.5 bg-gold"
                  />
                )}
              </button>
            ))}
          </div>

          <motion.div
            key={h.code}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
            className="lg:col-span-7 relative overflow-hidden border border-[var(--line)] min-h-[440px] spotlight"
          >
            <img
              src={h.image}
              alt={h.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-12">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[10px] tracking-eyebrow uppercase text-ivory">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald animate-pulse" />
                  Operating · {h.cities}
                </div>
                <span className="font-display text-6xl md:text-7xl text-gold leading-none">
                  {h.code}
                </span>
              </div>
              <div>
                <h3 className="font-display text-3xl md:text-5xl text-ivory leading-tight">
                  {h.name}
                </h3>
                <p className="mt-3 text-ivory/85 max-w-xl text-sm md:text-base leading-relaxed">
                  {h.focus}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Regulatory frameworks */}
        <div className="mt-16 md:mt-20">
          <div className="mb-6 flex items-baseline justify-between">
            <div className="text-[10px] tracking-eyebrow uppercase text-gold flex items-center gap-2">
              <span className="h-px w-6 bg-gold" />
              Regulatory frameworks · 6 · tap to open
            </div>
            <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
              Licensed footprint
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {frameworks.map((f, i) => (
              <motion.button
                key={f.slug}
                type="button"
                onClick={() => openFramework(i)}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid={`framework-${i}`}
                className="group relative text-left border border-[var(--line)] bg-ink-2/50 p-5 md:p-6 min-h-[190px] flex flex-col justify-between hover:border-gold/60 transition-colors outline-none focus-visible:border-gold"
              >
                <div>
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim flex items-center justify-between">
                    <span>{f.region}</span>
                    <ArrowUpRight
                      size={14}
                      className="opacity-40 group-hover:opacity-100 group-hover:text-gold transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </div>
                  <div className="mt-3 font-display text-2xl md:text-3xl text-ivory leading-tight">
                    {f.code}
                  </div>
                  <div className="mt-1 text-[10px] tracking-eyebrow uppercase text-gold">
                    {f.authority}
                  </div>
                </div>
                <p className="mt-4 text-xs md:text-sm text-ivory/60 leading-relaxed">
                  {f.desc}
                </p>
                <span className="absolute top-4 right-10 text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  0{i + 1}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="!bg-ink !text-ivory border-l border-[var(--line-strong)] w-full sm:!max-w-lg md:!max-w-xl p-0 overflow-y-auto"
          data-testid="framework-drawer"
        >
          {activeFw && (
            <div className="relative min-h-full flex flex-col">
              <div className="grain absolute inset-0 pointer-events-none" />
              <div className="relative p-8 md:p-10 border-b border-[var(--line)]">
                <div className="text-[10px] tracking-eyebrow uppercase text-gold flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Scale size={12} strokeWidth={1.5} />
                    {activeFw.region} · framework
                  </span>
                  <span className="text-ivory-dim">{activeFw.detail.read}</span>
                </div>
                <SheetTitle asChild>
                  <h3 className="mt-4 font-display text-3xl md:text-4xl leading-[1.1] tracking-tight text-ivory">
                    {activeFw.code}
                  </h3>
                </SheetTitle>
                <div className="mt-1 text-[10px] tracking-eyebrow uppercase text-emerald">
                  {activeFw.authority} · {activeFw.long_name}
                </div>
                <SheetDescription asChild>
                  <p className="mt-4 text-ivory/70 text-base leading-relaxed">
                    {activeFw.detail.headline}
                  </p>
                </SheetDescription>
              </div>

              <div className="relative p-8 md:p-10 space-y-8 flex-1">
                <section>
                  <div className="text-[10px] tracking-eyebrow uppercase text-emerald mb-3">
                    What it covers
                  </div>
                  <ul className="space-y-2.5">
                    {activeFw.detail.covers.map((it) => (
                      <li key={it} className="flex gap-3 text-sm md:text-base text-ivory/85 leading-relaxed">
                        <span className="text-emerald mt-1.5 shrink-0">✓</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section>
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-3">
                    What it doesn&rsquo;t
                  </div>
                  <ul className="space-y-2.5">
                    {activeFw.detail.doesnt.map((it) => (
                      <li key={it} className="flex gap-3 text-sm md:text-base text-ivory/70 leading-relaxed">
                        <span className="text-ivory-dim mt-1.5 shrink-0">—</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="border-l border-gold/50 pl-4">
                  <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-2">
                    Why it matters
                  </div>
                  <p className="text-ivory/85 italic leading-relaxed text-sm md:text-base">
                    {activeFw.detail.matters}
                  </p>
                </section>

                <section className="border-t border-[var(--line)] pt-6">
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-2">
                    Shareable link
                  </div>
                  <div className="flex items-center gap-3">
                    <code className="flex-1 text-xs md:text-sm text-ivory/70 font-mono bg-ink-2 border border-[var(--line)] px-3 py-2 truncate">
                      {typeof window !== "undefined"
                        ? `${window.location.origin}${window.location.pathname}#frameworks/${activeFw.slug}`
                        : `#frameworks/${activeFw.slug}`}
                    </code>
                    <button
                      type="button"
                      data-testid="framework-copy-link"
                      onClick={copyLink}
                      className="text-[10px] tracking-eyebrow uppercase text-gold gold-underline shrink-0 flex items-center gap-1"
                    >
                      <Copy size={11} strokeWidth={1.5} />
                      Copy
                    </button>
                  </div>
                </section>
              </div>

              <div className="relative p-6 md:p-8 border-t border-[var(--line)] bg-ink-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  Illustrative · counsel your own advisors
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={downloadBrief}
                    data-testid="framework-download-brief"
                    className="group inline-flex items-center gap-2 px-4 py-3 border border-gold/60 text-gold hover:bg-gold hover:text-ink transition-colors text-[11px] tracking-eyebrow uppercase"
                  >
                    <Download size={14} strokeWidth={1.5} />
                    Brief for counsel
                  </button>
                  <a
                    href="#workshop"
                    onClick={() => handleOpenChange(false)}
                    data-testid="framework-cta"
                    className="group inline-flex items-center gap-2 px-4 py-3 bg-gold text-ink font-medium hover:bg-gold-soft transition-colors text-[11px] tracking-eyebrow uppercase"
                  >
                    Discuss
                    <ArrowUpRight
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </a>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}
